import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { monthContractRevenue, monthCashCollected, monthJobCosts, monthOverhead, monthNetProfit } from '../lib/pnl'
import { toDateStr } from '../lib/dates'

const OVERHEAD_CATEGORIES = ['Insurance', 'Fuel', 'Vehicle Payment', 'Equipment Payment', 'Phone', 'Marketing', 'Tools/Supplies', 'Other']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const INCOME_CATEGORIES = ['Job Payment', 'Deposit', 'Tip', 'Refund Received', 'Other Income']
const EXPENSE_CATEGORIES = ['Materials', 'Fuel', 'Equipment', 'Vehicle Payment', 'Insurance', 'Phone', 'Marketing', 'Subcontractor', 'Payroll', 'Tools/Supplies', 'Office', 'Meals', 'Other Expense']
const ALL_TX_CATEGORIES = [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES]

export default function Financials() {
  const [tab, setTab] = useState('pl')
  const [year, setYear] = useState(new Date().getFullYear())
  const [overhead, setOverhead] = useState([])
  const [jobs, setJobs] = useState([])
  const [timeEntries, setTimeEntries] = useState([])
  const [newOverhead, setNewOverhead] = useState({ category: 'Insurance', description: '', amount: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), recurring: false })
  const [exportLoading, setExportLoading] = useState(false)

  // Transactions state
  const [transactions, setTransactions] = useState([])
  const [accounts, setAccounts] = useState([])
  const [txJobs, setTxJobs] = useState([])
  const [txLoading, setTxLoading] = useState(false)
  const [showAddTx, setShowAddTx] = useState(false)
  const [txSaving, setTxSaving] = useState(false)
  const [txError, setTxError] = useState('')
  const [editingTxId, setEditingTxId] = useState(null)
  const [filterAccount, setFilterAccount] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterMonth, setFilterMonth] = useState('')
  const [txSearch, setTxSearch] = useState('')
  const [txForm, setTxForm] = useState({
    date: toDateStr(new Date()),
    description: '', amount: '', type: 'expense',
    category: '', account_id: '', job_id: '', notes: ''
  })

  // Plaid state
  const [plaidConnecting, setPlaidConnecting] = useState(false)
  const [plaidAccounts, setPlaidAccounts] = useState([])
  const [plaidReviewQueue, setPlaidReviewQueue] = useState([])
  const [showPlaidReview, setShowPlaidReview] = useState(false)
  const [plaidImporting, setPlaidImporting] = useState(false)
  const [plaidError, setPlaidError] = useState('')
  const [fetchingPlaidTx, setFetchingPlaidTx] = useState(false)

  // CSV Import state
  const [showCsvImport, setShowCsvImport] = useState(false)
  const [csvPreview, setCsvPreview] = useState([])
  const [csvImporting, setCsvImporting] = useState(false)
  const [csvError, setCsvError] = useState('')
  const [csvAccount, setCsvAccount] = useState('')

  useEffect(() => { fetchData() }, [year])
  useEffect(() => { if (tab === 'transactions') fetchTransactions() }, [tab])

  async function fetchData() {
    const [overheadRes, jobsRes, timeRes, txRes] = await Promise.all([
      supabase.from('overhead_costs').select('*').eq('year', year).order('month').order('category'),
      supabase.from('jobs').select('*, job_costs(amount, category)').eq('status', 'completed').order('scheduled_date', { ascending: false }),
      supabase.from('time_entries').select('*, employee:profiles(full_name, hourly_rate)').not('clock_out', 'is', null),
      supabase.from('transactions').select('*').order('date', { ascending: false })
    ])
    setOverhead(overheadRes.data || [])
    setJobs(jobsRes.data || [])
    setTimeEntries(timeRes.data || [])
    setTransactions(txRes.data || [])
  }

  async function fetchTransactions() {
    setTxLoading(true)
    const [txRes, accRes, jobsRes, plaidRes] = await Promise.all([
      supabase.from('transactions').select('*, account:accounts(name), job:jobs(title)').order('date', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('accounts').select('*').order('name'),
      supabase.from('jobs').select('id, title, client:clients(name)').order('created_at', { ascending: false }),
      supabase.from('plaid_accounts').select('*').order('created_at')
    ])
    setTransactions(txRes.data || [])
    setAccounts(accRes.data || [])
    setTxJobs(jobsRes.data || [])
    setPlaidAccounts(plaidRes.data || [])
    setTxLoading(false)
  }

  // PLAID — connect bank account
  async function connectPlaid() {
    setPlaidConnecting(true)
    setPlaidError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/plaid-integration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'create_link_token' })
      })
      const data = await res.json()
      if (data.error) { setPlaidError(data.error); setPlaidConnecting(false); return }

      // Load Plaid Link script dynamically
      const script = document.createElement('script')
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'
      script.onload = () => {
        const handler = window.Plaid.create({
          token: data.link_token,
          onSuccess: async (public_token, metadata) => {
            const exchangeRes = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/plaid-integration`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
              },
              body: JSON.stringify({
                action: 'exchange_token',
                public_token,
                institution_name: metadata.institution?.name || 'Bank',
                accounts: metadata.accounts
              })
            })
            const exchangeData = await exchangeRes.json()
            if (exchangeData.error) { setPlaidError(exchangeData.error); } else { fetchTransactions() }
            setPlaidConnecting(false)
          },
          onExit: () => { setPlaidConnecting(false) }
        })
        handler.open()
      }
      document.head.appendChild(script)
    } catch (e) {
      setPlaidError('Failed to connect: ' + e.message)
      setPlaidConnecting(false)
    }
  }

  // PLAID — fetch transactions from connected account for review
  async function fetchPlaidTransactions(accountId) {
    setFetchingPlaidTx(true)
    setPlaidError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/plaid-integration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'get_transactions', account_id: accountId })
      })
      const data = await res.json()
      if (data.error) { setPlaidError(data.error); setFetchingPlaidTx(false); return }
      setPlaidReviewQueue(data.transactions.map(t => ({ ...t, selected: true })))
      setShowPlaidReview(true)
    } catch (e) {
      setPlaidError('Failed to fetch: ' + e.message)
    }
    setFetchingPlaidTx(false)
  }

  // PLAID — confirm selected transactions from review queue
  async function confirmPlaidTransactions() {
    setPlaidImporting(true)
    const toImport = plaidReviewQueue.filter(t => t.selected)
    for (const tx of toImport) {
      await supabase.from('transactions').insert([{
        date: tx.date,
        description: tx.description,
        amount: tx.amount,
        type: tx.type,
        category: tx.category,
        account_id: tx.account_id,
        plaid_transaction_id: tx.plaid_transaction_id,
        plaid_account_id: tx.plaid_account_id,
        status: 'confirmed'
      }])
    }
    setPlaidImporting(false)
    setShowPlaidReview(false)
    setPlaidReviewQueue([])
    fetchTransactions()
    fetchData()
  }

  function updatePlaidReviewRow(idx, field, val) {
    setPlaidReviewQueue(prev => prev.map((t, i) => i === idx ? { ...t, [field]: val } : t))
  }

  // Universal CSV parser — works with Chase, BofA, Wells Fargo, Capital One, etc.
  function parseCSVLine(line) {
    const cols = []
    let current = ''
    let inQuote = false
    for (const char of line) {
      if (char === '"') { inQuote = !inQuote }
      else if (char === ',' && !inQuote) { cols.push(current.trim()); current = '' }
      else { current += char }
    }
    cols.push(current.trim())
    return cols
  }

  function parseAmount(str) {
    if (!str) return NaN
    // Handle parentheses for negatives e.g. (123.45)
    const negative = str.includes('(') || str.trim().startsWith('-')
    const cleaned = str.replace(/[^0-9.]/g, '')
    const val = parseFloat(cleaned)
    return negative ? -val : val
  }

  function parseDate(str) {
    if (!str) return null
    // Try common formats: MM/DD/YYYY, YYYY-MM-DD, MM-DD-YYYY, DD/MM/YYYY, Month DD YYYY
    const formats = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,  // MM/DD/YYYY
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/,     // YYYY-MM-DD
      /^(\d{1,2})-(\d{1,2})-(\d{4})$/,     // MM-DD-YYYY
    ]
    for (const fmt of formats) {
      const m = str.match(fmt)
      if (m) {
        const d = new Date(str)
        if (!isNaN(d.getTime())) return d.toISOString().split('T')[0]
      }
    }
    // Fallback to Date constructor
    const d = new Date(str)
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0]
    return null
  }

  function detectColumns(headers) {
    // Score each header against known patterns for each column type
    const patterns = {
      date: ['transaction date', 'date', 'trans date', 'posting date', 'posted date', 'activity date', 'effective date'],
      description: ['description', 'merchant', 'payee', 'transaction description', 'details', 'memo', 'name', 'narration'],
      amount: ['amount', 'transaction amount', 'debit/credit', 'value'],
      debit: ['debit', 'withdrawal', 'charges', 'payment'],
      credit: ['credit', 'deposit', 'deposits'],
      category: ['category', 'transaction type', 'type'],
    }

    const result = {}
    for (const [col, keywords] of Object.entries(patterns)) {
      let bestIdx = -1
      let bestScore = -1
      headers.forEach((h, i) => {
        const clean = h.toLowerCase().trim()
        for (const kw of keywords) {
          if (clean === kw) { if (bestScore < 2) { bestScore = 2; bestIdx = i } }
          else if (clean.includes(kw)) { if (bestScore < 1) { bestScore = 1; bestIdx = i } }
        }
      })
      result[col] = bestIdx
    }
    return result
  }

  function handleCsvUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setCsvError('')
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const text = ev.target.result
        // Handle different line endings
        const lines = text.split(/\r?\n/).filter(l => l.trim())
        if (lines.length < 2) { setCsvError('CSV file appears empty'); return }

        // Skip any non-header rows at the top (some banks add bank name/account info before headers)
        let headerLineIdx = 0
        for (let i = 0; i < Math.min(5, lines.length); i++) {
          const cols = parseCSVLine(lines[i])
          // Header row usually has more columns and contains word-like values
          if (cols.length >= 3 && cols.some(c => /^[a-zA-Z]/.test(c.trim()))) {
            headerLineIdx = i
            break
          }
        }

        const headers = parseCSVLine(lines[headerLineIdx]).map(h => h.replace(/"/g, '').trim())
        const cols = detectColumns(headers)

        const parsed = []
        for (let i = headerLineIdx + 1; i < lines.length; i++) {
          const row = parseCSVLine(lines[i])
          if (row.length < 2) continue

          // Get date
          const dateStr = cols.date !== -1 ? row[cols.date] : row[0]
          const date = parseDate(dateStr)
          if (!date) continue

          // Get description
          const description = (cols.description !== -1 ? row[cols.description] : row[1] || '').replace(/"/g, '').trim()
          if (!description) continue

          // Get amount — handle split debit/credit columns (common in BofA, Wells Fargo)
          let amount
          if (cols.debit !== -1 && cols.credit !== -1) {
            // Separate debit and credit columns
            const debit = parseAmount(row[cols.debit])
            const credit = parseAmount(row[cols.credit])
            if (!isNaN(debit) && debit !== 0) amount = -Math.abs(debit)
            else if (!isNaN(credit) && credit !== 0) amount = Math.abs(credit)
            else continue
          } else if (cols.amount !== -1) {
            amount = parseAmount(row[cols.amount])
          } else {
            // Try to find any numeric column
            for (let j = 0; j < row.length; j++) {
              const v = parseAmount(row[j])
              if (!isNaN(v) && v !== 0) { amount = v; break }
            }
          }

          if (amount === undefined || isNaN(amount)) continue

          // Determine income vs expense
          // Most banks: negative = expense (debit), positive = income (credit)
          const type = amount >= 0 ? 'income' : 'expense'
          const absAmount = Math.abs(amount)

          // Get bank category if available
          const bankCategory = cols.category !== -1 ? (row[cols.category] || '') : ''
          const guessedCategory = guessCategory(description, bankCategory)
          // If guessed as transfer, override the type
          const isTransfer = guessedCategory === 'transfer'

          parsed.push({
            date,
            description,
            amount: absAmount,
            type: isTransfer ? 'transfer' : type,
            category: isTransfer ? '' : guessedCategory,
            account_id: csvAccount || '',
            selected: true
          })
        }

        if (parsed.length === 0) {
          setCsvError('No valid transactions found. Please check the file format — make sure it contains Date, Description, and Amount columns.')
          return
        }
        setCsvPreview(parsed)
        setShowCsvImport(true)
      } catch (err) {
        setCsvError('Failed to parse CSV: ' + err.message)
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  function guessCategory(description, bankCategory = '') {
    const d = (description + ' ' + bankCategory).toLowerCase()
    // Credit card payments are transfers, not income
    if (d.includes('payment thank you') || d.includes('autopay') || d.includes('online payment') || d.includes('mobile payment') || d.includes('automatic payment')) return 'transfer'
    if (d.includes('shell') || d.includes('chevron') || d.includes('exxon') || d.includes('mobil') || d.includes('speedway') || d.includes('circle k') || d.includes('gas station') || d.includes('fuel') || (d.includes('gas') && !d.includes('natural gas'))) return 'Fuel'
    if (d.includes('home depot') || d.includes('lowes') || d.includes('lumber') || d.includes('veseris') || d.includes('supply') || d.includes('material') || d.includes('hardware')) return 'Materials'
    if (d.includes('insurance') || d.includes('geico') || d.includes('progressive') || d.includes('state farm') || d.includes('allstate')) return 'Insurance'
    if (d.includes('at&t') || d.includes('verizon') || d.includes('t-mobile') || d.includes('sprint') || d.includes('telephone') || d.includes('cellular')) return 'Phone'
    if (d.includes('payroll') || d.includes('gusto') || d.includes('adp') || d.includes('paychex')) return 'Payroll'
    if (d.includes('facebook') || d.includes('google ads') || d.includes('yelp') || d.includes('advertising') || d.includes('marketing')) return 'Marketing'
    if (d.includes('restaurant') || d.includes('food & drink') || d.includes('pizza') || d.includes('taco') || d.includes('mcdonald') || d.includes('canes') || d.includes('chick-fil') || d.includes('subway') || d.includes('wendy') || d.includes('burger')) return 'Meals'
    if (d.includes('zelle') || d.includes('venmo') || d.includes('cash app')) return 'Job Payment'
    if (d.includes('amazon') || d.includes('staples') || d.includes('office depot')) return 'Office'
    if (d.includes('truck') || d.includes('vehicle') || d.includes('auto') || d.includes('car wash') || d.includes('ford') || d.includes('chevy') || d.includes('toyota')) return 'Vehicle Payment'
    if (d.includes('equipment') || d.includes('rental') || d.includes('sunbelt') || d.includes('united rentals')) return 'Equipment'
    if (d.includes('repair') || d.includes('maintenance')) return 'Materials'
    return 'Other Expense'
  }

  async function confirmCsvImport() {
    setCsvImporting(true)
    setCsvError('')
    const toImport = csvPreview.filter(t => t.selected)
    let successCount = 0
    let failCount = 0
    for (const tx of toImport) {
      const { error } = await supabase.from('transactions').insert([{
        date: tx.date,
        description: tx.description,
        amount: tx.amount,
        type: tx.type,
        category: tx.category || null,
        account_id: tx.account_id || null,
        status: 'confirmed'
      }])
      if (error) { console.error('Insert error:', error); failCount++ }
      else successCount++
    }
    setCsvImporting(false)
    if (failCount > 0) {
      setCsvError(`${failCount} transactions failed to import. Check console for details. ${successCount} succeeded.`)
    } else {
      setShowCsvImport(false)
      setCsvPreview([])
    }
    fetchTransactions()
    fetchData()
  }

  function updateCsvRow(idx, field, val) {
    setCsvPreview(prev => prev.map((t, i) => i === idx ? { ...t, [field]: val } : t))
  }

  function resetTxForm() {
    setTxForm({ date: toDateStr(new Date()), description: '', amount: '', type: 'expense', category: '', account_id: '', job_id: '', notes: '' })
    setEditingTxId(null)
    setTxError('')
  }

  function setTx(field, val) { setTxForm(f => ({ ...f, [field]: val })) }

  async function handleSaveTx() {
    if (!txForm.description || !txForm.amount || !txForm.date) { setTxError('Date, description and amount are required'); return }
    setTxSaving(true)
    setTxError('')
    const payload = {
      date: txForm.date,
      description: txForm.description,
      amount: parseFloat(txForm.amount),
      type: txForm.type,
      category: txForm.category || null,
      account_id: txForm.account_id || null,
      job_id: txForm.job_id || null,
      notes: txForm.notes || null
    }
    const { error } = editingTxId
      ? await supabase.from('transactions').update(payload).eq('id', editingTxId)
      : await supabase.from('transactions').insert([payload])
    if (error) { setTxError(error.message); setTxSaving(false); return }
    setTxSaving(false)
    setShowAddTx(false)
    resetTxForm()
    fetchTransactions()
  }

  async function handleDeleteTx(id) {
    if (!window.confirm('Delete this transaction?')) return
    const { error } = await supabase.from('transactions').delete().eq('id', id)
    if (error) { alert('Delete failed: ' + error.message); return }
    fetchTransactions()
  }

  function startEditTx(tx) {
    setTxForm({ date: tx.date, description: tx.description, amount: tx.amount, type: tx.type, category: tx.category || '', account_id: tx.account_id || '', job_id: tx.job_id || '', notes: tx.notes || '' })
    setEditingTxId(tx.id)
    setShowAddTx(true)
    setTxError('')
  }

  // P&L helpers — thin wrappers over src/lib/pnl.js, binding the year selector.
  // FA-011: Contract Revenue and Cash Collected are always kept separate.
  function monthPLContractRevenue(m) { return monthContractRevenue(jobs, m, year) }
  function monthPLCashCollected(m) { return monthCashCollected(transactions, m, year) }
  function monthPLJobCosts(m) { return monthJobCosts(jobs, timeEntries, transactions, m, year) }
  function monthPLOverhead(m) { return monthOverhead(overhead, m) }

  async function addOverhead() {
    if (!newOverhead.description || !newOverhead.amount) return
    await supabase.from('overhead_costs').insert([{ ...newOverhead, amount: parseFloat(newOverhead.amount) }])
    setNewOverhead(o => ({ ...o, description: '', amount: '' }))
    fetchData()
  }

  async function deleteOverhead(id) {
    if (!window.confirm('Delete this overhead entry?')) return
    await supabase.from('overhead_costs').delete().eq('id', id)
    fetchData()
  }

  async function exportPayroll() {
    setExportLoading(true)
    const now = new Date()
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay()); weekStart.setHours(0, 0, 0, 0)
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23, 59, 59, 999)
    const { data: entries } = await supabase.from('time_entries').select('*, employee:profiles(full_name, hourly_rate)').gte('clock_in', weekStart.toISOString()).lte('clock_in', weekEnd.toISOString()).not('clock_out', 'is', null)
    const empMap = {}
    for (const entry of (entries || [])) {
      const name = entry.employee?.full_name || 'Unknown'
      const rate = entry.employee?.hourly_rate || 0
      const hours = (new Date(entry.clock_out) - new Date(entry.clock_in)) / 3600000
      if (!empMap[name]) empMap[name] = { name, hours: 0, rate }
      empMap[name].hours += hours
    }
    const weekEndStr = weekEnd.toLocaleDateString()
    let csv = 'Employee,Regular Hours,Hourly Rate,Gross Pay,Week Ending\n'
    for (const emp of Object.values(empMap)) {
      csv += `"${emp.name}",${emp.hours.toFixed(2)},${emp.rate},${(emp.hours * emp.rate).toFixed(2)},"${weekEndStr}"\n`
    }
    if (Object.keys(empMap).length === 0) csv += '(No time entries found for this week)\n'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `tlc_payroll_${toDateStr(now)}.csv`; a.click()
    setExportLoading(false)
  }

  const totalContractRevenue = MONTHS.reduce((s, _, i) => s + monthPLContractRevenue(i), 0)
  const totalCashCollected = MONTHS.reduce((s, _, i) => s + monthPLCashCollected(i), 0)
  const totalJobCosts = MONTHS.reduce((s, _, i) => s + monthPLJobCosts(i), 0)
  const totalOverheadYear = overhead.reduce((s, o) => s + (o.amount || 0), 0)
  const netProfit = monthNetProfit(totalContractRevenue, totalJobCosts, totalOverheadYear)

  // Transaction filters — exclude transfers from income/expense totals
  const filteredTx = transactions.filter(tx => {
    const matchAccount = !filterAccount || tx.account_id === filterAccount
    const matchType = !filterType || tx.type === filterType
    const matchCat = !filterCategory || tx.category === filterCategory
    const matchSearch = !txSearch || tx.description.toLowerCase().includes(txSearch.toLowerCase()) || (tx.notes || '').toLowerCase().includes(txSearch.toLowerCase())
    const matchMonth = !filterMonth || tx.date?.startsWith(filterMonth)
    return matchAccount && matchType && matchCat && matchSearch && matchMonth
  })

  // Exclude transfers from income/expense totals
  const txTotalIncome = filteredTx.filter(t => t.type === 'income').reduce((s, t) => s + (t.amount || 0), 0)
  const txTotalExpenses = filteredTx.filter(t => t.type === 'expense').reduce((s, t) => s + (t.amount || 0), 0)
  const txNetCashFlow = txTotalIncome - txTotalExpenses

  const categoryBreakdown = {}
  filteredTx.filter(t => t.type === 'expense').forEach(t => {
    const cat = t.category || 'Uncategorized'
    categoryBreakdown[cat] = (categoryBreakdown[cat] || 0) + (t.amount || 0)
  })
  const topCategories = Object.entries(categoryBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 5)

  // Account balances — only count transactions with an account_id
  const accountBalances = {}
  transactions.forEach(tx => {
    if (!tx.account_id) return
    if (!accountBalances[tx.account_id]) accountBalances[tx.account_id] = 0
    if (tx.type === 'transfer') return // transfers don't affect balance
    accountBalances[tx.account_id] += tx.type === 'income' ? tx.amount : -tx.amount
  })

  // Count unassigned transactions
  const unassignedCount = transactions.filter(t => !t.account_id).length

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Financials</h1>
          <p style={styles.sub}>Year: {year}</p>
        </div>
        <div style={styles.headerRight}>
          <select style={styles.yearSelect} value={year} onChange={e => setYear(parseInt(e.target.value))}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={styles.exportBtn} onClick={exportPayroll} disabled={exportLoading}>
            {exportLoading ? 'Exporting...' : '📤 Export Payroll CSV'}
          </button>
        </div>
      </div>

      <div style={styles.summaryGrid}>
        <SummaryCard label="Contract Revenue" value={`$${totalContractRevenue.toLocaleString()}`} color="#16a34a" icon="📄" />
        <SummaryCard label="Cash Collected" value={`$${totalCashCollected.toLocaleString()}`} color="#0369a1" icon="💰" />
        <SummaryCard label="Job Costs" value={`$${totalJobCosts.toLocaleString()}`} color="#2563eb" icon="🔨" />
        <SummaryCard label="Overhead" value={`$${totalOverheadYear.toLocaleString()}`} color="#d97706" icon="🏢" />
        <SummaryCard label="Net Profit" value={`$${netProfit.toLocaleString()}`} color={netProfit >= 0 ? '#16a34a' : '#dc2626'} icon="📈" />
      </div>

      <div style={styles.tabs}>
        {[
          { key: 'pl', label: 'P&L by Month' },
          { key: 'transactions', label: '💳 Transactions' },
          { key: 'overhead', label: 'Overhead' },
          { key: 'payroll', label: 'Payroll Export' }
        ].map(t => (
          <button key={t.key} style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* P&L Tab */}
      {tab === 'pl' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Profit & Loss — {year}</h2>
          <div style={styles.plTable}>
            <div style={styles.plHeader}><span>Month</span><span>Contract Revenue</span><span>Cash Collected</span><span>Job Costs</span><span>Overhead</span><span>Net Profit</span></div>
            {MONTHS.map((m, i) => {
              const contractRev = monthPLContractRevenue(i); const cashCollected = monthPLCashCollected(i)
              const jc = monthPLJobCosts(i); const oh = monthPLOverhead(i)
              const net = monthNetProfit(contractRev, jc, oh)
              return (
                <div key={m} style={styles.plRow}>
                  <span style={styles.plMonth}>{m}</span>
                  <span style={styles.plVal}>${contractRev.toLocaleString()}</span>
                  <span style={styles.plVal}>${cashCollected.toLocaleString()}</span>
                  <span style={styles.plVal}>${jc.toLocaleString()}</span>
                  <span style={styles.plVal}>${oh.toLocaleString()}</span>
                  <span style={{ ...styles.plNet, color: net >= 0 ? '#16a34a' : '#dc2626' }}>${net.toLocaleString()}</span>
                </div>
              )
            })}
            <div style={styles.plTotal}>
              <span>TOTAL</span><span>${totalContractRevenue.toLocaleString()}</span><span>${totalCashCollected.toLocaleString()}</span><span>${totalJobCosts.toLocaleString()}</span><span>${totalOverheadYear.toLocaleString()}</span>
              <span style={{ color: netProfit >= 0 ? '#16a34a' : '#dc2626' }}>${netProfit.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Transactions Tab */}
      {tab === 'transactions' && (
        <div>
          {/* Summary Cards */}
          <div style={styles.txSummaryGrid}>
            <div style={{ ...styles.txSummaryCard, borderTopColor: '#16a34a' }}>
              <div style={styles.txSummaryLabel}>Income</div>
              <div style={{ ...styles.txSummaryValue, color: '#16a34a' }}>${txTotalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={styles.txSummaryNote}>{filteredTx.filter(t => t.type === 'income').length} transactions</div>
            </div>
            <div style={{ ...styles.txSummaryCard, borderTopColor: '#dc2626' }}>
              <div style={styles.txSummaryLabel}>Expenses</div>
              <div style={{ ...styles.txSummaryValue, color: '#dc2626' }}>${txTotalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={styles.txSummaryNote}>{filteredTx.filter(t => t.type === 'expense').length} transactions</div>
            </div>
            <div style={{ ...styles.txSummaryCard, borderTopColor: txNetCashFlow >= 0 ? '#2563eb' : '#dc2626' }}>
              <div style={styles.txSummaryLabel}>Net Cash Flow</div>
              <div style={{ ...styles.txSummaryValue, color: txNetCashFlow >= 0 ? '#2563eb' : '#dc2626' }}>
                {txNetCashFlow >= 0 ? '+' : ''}${txNetCashFlow.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={styles.txSummaryNote}>{filterMonth ? 'filtered period' : 'all time'}</div>
            </div>
          </div>

          {/* Account Balances */}
          {accounts.length > 0 && (
            <div>
              <div style={styles.accountsRow}>
                {accounts.map(acc => {
                  const bal = accountBalances[acc.id] || 0
                  return (
                    <div key={acc.id} style={styles.accountChip}>
                      <span style={styles.accountName}>{acc.name}</span>
                      <span style={{ ...styles.accountBal, color: bal >= 0 ? '#16a34a' : '#dc2626' }}>
                        {bal >= 0 ? '+' : ''}${Math.abs(bal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )
                })}
              </div>
              {unassignedCount > 0 && (
                <div style={styles.unassignedWarning}>
                  ⚠️ {unassignedCount} transactions have no account assigned — balances may be incomplete.
                  <select style={{ ...styles.txFilterInput, marginLeft: '10px' }} defaultValue="" onChange={async e => {
                    if (!e.target.value) return
                    if (!window.confirm(`Assign all ${unassignedCount} unassigned transactions to this account?`)) return
                    const { error } = await supabase.from('transactions').update({ account_id: e.target.value }).is('account_id', null)
                    if (error) alert('Failed: ' + error.message)
                    else fetchTransactions()
                  }}>
                    <option value="">Assign all to account...</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Top Categories */}
          {topCategories.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Top Expense Categories</h3>
              <div style={styles.categoryList}>
                {topCategories.map(([cat, amt]) => (
                  <div key={cat} style={styles.categoryRow}>
                    <span style={styles.categoryName}>{cat}</span>
                    <div style={styles.categoryBarWrap}>
                      <div style={{ ...styles.categoryBar, width: `${Math.min(100, (amt / txTotalExpenses) * 100)}%` }} />
                    </div>
                    <span style={styles.categoryAmt}>${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plaid + CSV Import Bar */}
          <div style={styles.importBar}>
            <div style={styles.importBarLeft}>
              <span style={styles.importBarLabel}>Import Transactions:</span>
              <button style={styles.plaidBtn} onClick={connectPlaid} disabled={plaidConnecting}>
                {plaidConnecting ? '🔄 Connecting...' : '🏦 Connect Bank (Plaid)'}
              </button>
              <label style={styles.csvBtn}>
                📄 Import CSV (Chase)
                <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} />
              </label>
              {plaidAccounts.length > 0 && (
                <select style={styles.txFilterInput} onChange={e => e.target.value && fetchPlaidTransactions(e.target.value)} defaultValue="">
                  <option value="">🔄 Sync from connected bank...</option>
                  {plaidAccounts.map(a => <option key={a.id} value={a.account_id}>{a.institution_name} — {a.account_name} (...{a.mask})</option>)}
                </select>
              )}
            </div>
            {plaidError && <div style={styles.plaidError}>{plaidError}</div>}
          </div>

          {/* CSV Preview / Review */}
          {showCsvImport && csvPreview.length > 0 && (
            <div style={styles.reviewCard}>
              <div style={styles.reviewHeader}>
                <div>
                  <h3 style={styles.reviewTitle}>📄 CSV Import Preview</h3>
                  <p style={styles.reviewSub}>{csvPreview.filter(t => t.selected).length} of {csvPreview.length} transactions selected</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div>
                    <label style={styles.label}>Import to Account</label>
                    <select style={styles.input} value={csvAccount} onChange={e => { setCsvAccount(e.target.value); setCsvPreview(p => p.map(t => ({ ...t, account_id: e.target.value }))) }}>
                      <option value="">Select account...</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  <button style={styles.closeBtn} onClick={() => { setShowCsvImport(false); setCsvPreview([]) }}>✕</button>
                </div>
              </div>
              {csvError && <div style={styles.txErrorMsg}>{csvError}</div>}
              <div style={styles.reviewActions}>
                <button style={styles.selectAllBtn} onClick={() => setCsvPreview(p => p.map(t => ({ ...t, selected: true })))}>Select All</button>
                <button style={styles.selectAllBtn} onClick={() => setCsvPreview(p => p.map(t => ({ ...t, selected: false })))}>Deselect All</button>
              </div>
              <div style={styles.reviewList}>
                {csvPreview.map((tx, idx) => (
                  <div key={idx} style={{ ...styles.reviewRow, opacity: tx.selected ? 1 : 0.4 }}>
                    <input type="checkbox" checked={tx.selected} onChange={e => updateCsvRow(idx, 'selected', e.target.checked)} style={{ flexShrink: 0 }} />
                    <span style={styles.reviewDate}>{tx.date}</span>
                    <span style={styles.reviewDesc}>{tx.description}</span>
                    <select style={styles.reviewSelect} value={tx.type} onChange={e => updateCsvRow(idx, 'type', e.target.value)}>
                      <option value="income">Income</option>
                      <option value="expense">Expense</option>
                      <option value="transfer">Transfer</option>
                    </select>
                    <select style={styles.reviewSelect} value={tx.category} onChange={e => updateCsvRow(idx, 'category', e.target.value)}>
                      <option value="">Category...</option>
                      <optgroup label="Income">{INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                      <optgroup label="Expenses">{EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                    </select>
                    <span style={{ ...styles.reviewAmt, color: tx.type === 'income' ? '#16a34a' : '#dc2626' }}>
                      {tx.type === 'income' ? '+' : '-'}${tx.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div style={styles.reviewFooter}>
                <button style={styles.cancelBtn} onClick={() => { setShowCsvImport(false); setCsvPreview([]) }}>Cancel</button>
                <button style={styles.confirmImportBtn} onClick={confirmCsvImport} disabled={csvImporting || csvPreview.filter(t => t.selected).length === 0}>
                  {csvImporting ? 'Importing...' : `✓ Import ${csvPreview.filter(t => t.selected).length} Transactions`}
                </button>
              </div>
            </div>
          )}

          {/* Plaid Review Queue */}
          {showPlaidReview && plaidReviewQueue.length > 0 && (
            <div style={styles.reviewCard}>
              <div style={styles.reviewHeader}>
                <div>
                  <h3 style={styles.reviewTitle}>🏦 Bank Transactions — Review & Confirm</h3>
                  <p style={styles.reviewSub}>{plaidReviewQueue.filter(t => t.selected).length} of {plaidReviewQueue.length} new transactions to import</p>
                </div>
                <button style={styles.closeBtn} onClick={() => { setShowPlaidReview(false); setPlaidReviewQueue([]) }}>✕</button>
              </div>
              <div style={styles.reviewActions}>
                <button style={styles.selectAllBtn} onClick={() => setPlaidReviewQueue(p => p.map(t => ({ ...t, selected: true })))}>Select All</button>
                <button style={styles.selectAllBtn} onClick={() => setPlaidReviewQueue(p => p.map(t => ({ ...t, selected: false })))}>Deselect All</button>
              </div>
              <div style={styles.reviewList}>
                {plaidReviewQueue.map((tx, idx) => (
                  <div key={idx} style={{ ...styles.reviewRow, opacity: tx.selected ? 1 : 0.4 }}>
                    <input type="checkbox" checked={tx.selected} onChange={e => updatePlaidReviewRow(idx, 'selected', e.target.checked)} style={{ flexShrink: 0 }} />
                    <span style={styles.reviewDate}>{tx.date}</span>
                    <span style={styles.reviewDesc}>{tx.description}</span>
                    <select style={styles.reviewSelect} value={tx.type} onChange={e => updatePlaidReviewRow(idx, 'type', e.target.value)}>
                      <option value="income">Income</option>
                      <option value="expense">Expense</option>
                      <option value="transfer">Transfer</option>
                    </select>
                    <select style={styles.reviewSelect} value={tx.category} onChange={e => updatePlaidReviewRow(idx, 'category', e.target.value)}>
                      <option value="">Category...</option>
                      <optgroup label="Income">{INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                      <optgroup label="Expenses">{EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                    </select>
                    <span style={{ ...styles.reviewAmt, color: tx.type === 'income' ? '#16a34a' : '#dc2626' }}>
                      {tx.type === 'income' ? '+' : '-'}${tx.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div style={styles.reviewFooter}>
                <button style={styles.cancelBtn} onClick={() => { setShowPlaidReview(false); setPlaidReviewQueue([]) }}>Cancel</button>
                <button style={styles.confirmImportBtn} onClick={confirmPlaidTransactions} disabled={plaidImporting || plaidReviewQueue.filter(t => t.selected).length === 0}>
                  {plaidImporting ? 'Importing...' : `✓ Confirm ${plaidReviewQueue.filter(t => t.selected).length} Transactions`}
                </button>
              </div>
            </div>
          )}

          {fetchingPlaidTx && <div style={styles.txLoading}>Fetching bank transactions...</div>}

          {/* Filters + Add Button */}
          <div style={styles.txToolbar}>
            <div style={styles.txFilters}>
              <input id="tx-search" name="tx-search" style={styles.txSearchInput} placeholder="🔍 Search..." value={txSearch} onChange={e => setTxSearch(e.target.value)} />
              <input style={styles.txFilterInput} type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} />
              <select style={styles.txFilterInput} value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
              <select style={styles.txFilterInput} value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
                <option value="">All Accounts</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <select style={styles.txFilterInput} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                <option value="">All Categories</option>
                {ALL_TX_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {(filterAccount || filterType || filterCategory || filterMonth || txSearch) && (
                <button style={styles.clearBtn} onClick={() => { setFilterAccount(''); setFilterType(''); setFilterCategory(''); setFilterMonth(''); setTxSearch('') }}>✕ Clear</button>
              )}
            </div>
            <button style={styles.addTxBtn} onClick={() => { resetTxForm(); setShowAddTx(true) }}>+ Add Transaction</button>
          </div>

          {/* Add/Edit Form */}
          {showAddTx && (
            <div style={styles.txFormCard}>
              <div style={styles.txFormHeader}>
                <h3 style={styles.txFormTitle}>{editingTxId ? 'Edit Transaction' : 'New Transaction'}</h3>
                <button style={styles.closeBtn} onClick={() => { setShowAddTx(false); resetTxForm() }}>✕</button>
              </div>
              {txError && <div style={styles.txErrorMsg}>{txError}</div>}
              <div style={styles.txFormGrid}>
                <div>
                  <label style={styles.label}>Date *</label>
                  <input id="tx-date" name="tx-date" style={styles.input} type="date" value={txForm.date} onChange={e => setTx('date', e.target.value)} />
                </div>
                <div>
                  <label style={styles.label}>Type *</label>
                  <select style={styles.input} value={txForm.type} onChange={e => setTx('type', e.target.value)}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                    <option value="transfer">Transfer (CC Payment)</option>
                  </select>
                </div>
                <div style={styles.fieldFull}>
                  <label style={styles.label}>Description *</label>
                  <input id="tx-desc" name="tx-desc" style={styles.input} value={txForm.description} onChange={e => setTx('description', e.target.value)} placeholder="e.g. Fuel purchase, client payment" />
                </div>
                <div>
                  <label style={styles.label}>Amount ($) *</label>
                  <input id="tx-amount" name="tx-amount" style={styles.input} type="number" step="0.01" value={txForm.amount} onChange={e => setTx('amount', e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label style={styles.label}>Category</label>
                  <select style={styles.input} value={txForm.category} onChange={e => setTx('category', e.target.value)}>
                    <option value="">Select...</option>
                    <optgroup label="Income">{INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                    <optgroup label="Expenses">{EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Account</label>
                  <select style={styles.input} value={txForm.account_id} onChange={e => setTx('account_id', e.target.value)}>
                    <option value="">Select account...</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Link to Job</label>
                  <select style={styles.input} value={txForm.job_id} onChange={e => setTx('job_id', e.target.value)}>
                    <option value="">No job</option>
                    {txJobs.map(j => <option key={j.id} value={j.id}>{j.title}{j.client ? ` — ${j.client.name}` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Notes</label>
                  <input id="tx-notes" name="tx-notes" style={styles.input} value={txForm.notes} onChange={e => setTx('notes', e.target.value)} placeholder="Optional..." />
                </div>
              </div>
              <div style={styles.txFormActions}>
                <button style={styles.cancelBtn} onClick={() => { setShowAddTx(false); resetTxForm() }}>Cancel</button>
                <button style={styles.saveTxBtn} onClick={handleSaveTx} disabled={txSaving}>{txSaving ? 'Saving...' : editingTxId ? 'Save Changes' : 'Add Transaction'}</button>
              </div>
            </div>
          )}

          {/* Transaction List */}
          {txLoading ? <div style={styles.txLoading}>Loading transactions...</div> : (
            <div style={styles.txList}>
              {filteredTx.length === 0 && <div style={styles.noData}>No transactions found. Add your first one above.</div>}
              {filteredTx.map(tx => (
                <div key={tx.id} style={styles.txRow}>
                  <div style={{
                    ...styles.txTypeIcon,
                    background: tx.type === 'income' ? '#dcfce7' : tx.type === 'transfer' ? '#f3f4f6' : '#fef2f2',
                    color: tx.type === 'income' ? '#16a34a' : tx.type === 'transfer' ? '#6b7280' : '#dc2626'
                  }}>
                    {tx.type === 'income' ? '↑' : tx.type === 'transfer' ? '⇄' : '↓'}
                  </div>
                  <div style={styles.txInfo}>
                    <span style={styles.txDesc}>{tx.description}</span>
                    <div style={styles.txMeta}>
                      <span>{new Date(tx.date).toLocaleDateString()}</span>
                      {tx.type === 'transfer' && <span style={{ ...styles.txCat, background: '#e5e7eb', color: '#6b7280' }}>Transfer</span>}
                      {tx.category && tx.type !== 'transfer' && <span style={styles.txCat}>{tx.category}</span>}
                      {tx.account && <span>💳 {tx.account.name}</span>}
                      {tx.job && <span style={{ color: '#2d4a2d', fontWeight: '600' }}>🔨 {tx.job.title}</span>}
                    </div>
                    {tx.notes && <span style={styles.txNotes}>{tx.notes}</span>}
                  </div>
                  <div style={styles.txRight}>
                    <span style={{ ...styles.txAmount, color: tx.type === 'income' ? '#16a34a' : tx.type === 'transfer' ? '#6b7280' : '#dc2626' }}>
                      {tx.type === 'income' ? '+' : tx.type === 'transfer' ? '' : '-'}${(tx.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <div style={styles.txActions}>
                      <button style={styles.txEditBtn} onClick={() => startEditTx(tx)}>✏️</button>
                      <button style={styles.txDeleteBtn} onClick={() => handleDeleteTx(tx.id)}>✕</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Overhead Tab */}
      {tab === 'overhead' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Overhead Costs — {year}</h2>
          <div style={styles.addCostForm}>
            <select style={styles.select} value={newOverhead.category} onChange={e => setNewOverhead(o => ({ ...o, category: e.target.value }))}>
              {OVERHEAD_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input id="overhead-desc" name="overhead-desc" style={styles.input} placeholder="Description" value={newOverhead.description} onChange={e => setNewOverhead(o => ({ ...o, description: e.target.value }))} />
            <select style={styles.select} value={newOverhead.month} onChange={e => setNewOverhead(o => ({ ...o, month: parseInt(e.target.value) }))}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <input id="overhead-amount" name="overhead-amount" style={{ ...styles.input, width: '100px' }} placeholder="Amount $" type="number" value={newOverhead.amount} onChange={e => setNewOverhead(o => ({ ...o, amount: e.target.value }))} />
            <button style={styles.addBtn} onClick={addOverhead}>+ Add</button>
          </div>
          <div style={styles.overheadList}>
            {overhead.map(o => (
              <div key={o.id} style={styles.overheadRow}>
                <span style={styles.overheadMonth}>{MONTHS[o.month - 1]}</span>
                <span style={styles.overheadCat}>{o.category}</span>
                <span style={styles.overheadDesc}>{o.description}</span>
                <span style={styles.overheadAmt}>${(o.amount || 0).toFixed(2)}</span>
                <button style={styles.deleteOverheadBtn} onClick={() => deleteOverhead(o.id)}>✕</button>
              </div>
            ))}
            {overhead.length === 0 && <p style={styles.noData}>No overhead costs added yet.</p>}
          </div>
        </div>
      )}

      {/* Payroll Tab */}
      {tab === 'payroll' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Payroll Export for QuickBooks</h2>
          <p style={styles.payrollNote}>Export a CSV file with employee hours for the current week. Import this into QuickBooks to process payroll taxes.</p>
          <div style={styles.payrollActions}>
            <button style={styles.exportBigBtn} onClick={exportPayroll} disabled={exportLoading}>
              {exportLoading ? 'Exporting...' : '📤 Export This Week\'s Hours (CSV)'}
            </button>
          </div>
          <div style={styles.payrollInstructions}>
            <h3 style={styles.instrTitle}>How to import into QuickBooks:</h3>
            <ol style={styles.instrList}>
              <li>Click "Export This Week's Hours" above</li>
              <li>In QuickBooks, go to Employees → Payroll → Import</li>
              <li>Select the CSV file you downloaded</li>
              <li>Map columns: Employee = Name, Regular Hours = Hours, Rate = Hourly Rate</li>
              <li>Review and run payroll as normal</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  )
}

function SummaryCard({ label, value, color, icon }) {
  return (
    <div style={{ ...styles.summaryCard, borderTopColor: color }}>
      <div style={styles.summaryIcon}>{icon}</div>
      <div style={{ ...styles.summaryValue, color }}>{value}</div>
      <div style={styles.summaryLabel}>{label}</div>
    </div>
  )
}

const styles = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  headerRight: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  yearSelect: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  exportBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  summaryCard: { background: 'white', borderRadius: '12px', padding: '20px', borderTop: '4px solid', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' },
  summaryIcon: { fontSize: '24px', marginBottom: '8px' },
  summaryValue: { fontSize: '24px', fontWeight: '700', marginBottom: '4px' },
  summaryLabel: { fontSize: '12px', color: '#6b7c6b', textTransform: 'uppercase', letterSpacing: '0.5px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: { padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' },
  tabActive: { background: '#2d4a2d', color: 'white', borderColor: '#2d4a2d' },
  section: { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '16px' },
  sectionTitle: { margin: '0 0 16px', fontSize: '16px', fontWeight: '700', color: '#1a2e1a' },
  // Dense 6-column monthly P&L table -- desktop-appropriate analysis content per
  // Docs/UI_GUIDE.md "Mobile vs Desktop Purpose". Horizontal scroll (UI_GUIDE.md's
  // own documented alternative to card-stacking for tables) rather than a full
  // mobile card redesign -- minWidth forces the scrollbar instead of letting the
  // grid columns compress unreadably thin.
  plTable: { overflow: 'auto' },
  plHeader: { display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 1fr', gap: '8px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '4px', fontSize: '12px', fontWeight: '700', color: '#6b7c6b', textTransform: 'uppercase', minWidth: '620px' },
  plRow: { display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 1fr', gap: '8px', padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', minWidth: '620px' },
  plMonth: { fontWeight: '600', color: '#374151' },
  plVal: { color: '#374151' },
  plNet: { fontWeight: '700' },
  plTotal: { display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 1fr', gap: '8px', padding: '12px', background: '#f9fafb', borderRadius: '8px', marginTop: '8px', fontSize: '14px', fontWeight: '700', color: '#1a2e1a', minWidth: '620px' },
  addCostForm: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px' },
  select: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  input: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', flex: 1, width: '100%', boxSizing: 'border-box' },
  addBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  overheadList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  overheadRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px' },
  overheadMonth: { fontWeight: '700', color: '#2d4a2d', width: '36px', flexShrink: 0 },
  overheadCat: { background: '#e5e7eb', color: '#374151', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', flexShrink: 0 },
  overheadDesc: { flex: 1, fontSize: '14px', color: '#374151' },
  overheadAmt: { fontWeight: '700', color: '#1a2e1a' },
  deleteOverheadBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px', padding: '0 4px' },
  noData: { color: '#6b7c6b', fontSize: '14px', textAlign: 'center', padding: '32px 0' },
  payrollNote: { color: '#6b7c6b', fontSize: '14px', marginBottom: '20px' },
  payrollActions: { marginBottom: '24px' },
  exportBigBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '16px 28px', borderRadius: '10px', cursor: 'pointer', fontSize: '16px', fontWeight: '600' },
  payrollInstructions: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '20px' },
  instrTitle: { margin: '0 0 12px', fontSize: '16px', fontWeight: '700', color: '#1a2e1a' },
  instrList: { margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: '#374151' },
  // Transaction styles
  txSummaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' },
  txSummaryCard: { background: 'white', borderRadius: '10px', padding: '16px', borderTop: '3px solid', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  txSummaryLabel: { fontSize: '12px', color: '#6b7c6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  txSummaryValue: { fontSize: '22px', fontWeight: '700', marginBottom: '4px' },
  txSummaryNote: { fontSize: '12px', color: '#9ca3af' },
  accountsRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' },
  accountChip: { background: 'white', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: '2px' },
  accountName: { fontSize: '11px', color: '#6b7c6b', fontWeight: '600', textTransform: 'uppercase' },
  accountBal: { fontSize: '16px', fontWeight: '700' },
  categoryList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  categoryRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  categoryName: { fontSize: '13px', color: '#374151', width: '140px', flexShrink: 0 },
  categoryBarWrap: { flex: 1, background: '#f3f4f6', borderRadius: '4px', height: '8px', overflow: 'hidden' },
  categoryBar: { background: '#2d4a2d', height: '100%', borderRadius: '4px' },
  categoryAmt: { fontSize: '13px', fontWeight: '700', color: '#374151', width: '80px', textAlign: 'right', flexShrink: 0 },
  txToolbar: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' },
  txFilters: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', flex: 1 },
  txSearchInput: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', minWidth: '140px' },
  txFilterInput: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit' },
  clearBtn: { background: 'none', border: '1px solid #e5e7eb', color: '#6b7c6b', padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  addTxBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '9px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', flexShrink: 0 },
  txFormCard: { background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid #2d4a2d' },
  txFormHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  txFormTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7c6b' },
  txErrorMsg: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' },
  txFormGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' },
  fieldFull: { gridColumn: '1 / -1' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  txFormActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', borderRadius: '8px', border: '1.5px solid #d1d5db', background: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer', color: '#374151' },
  saveTxBtn: { padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#2d4a2d', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  txLoading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  txList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  txRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', background: 'white', borderRadius: '10px', padding: '14px 16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  txTypeIcon: { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '700', flexShrink: 0 },
  txInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' },
  txDesc: { fontWeight: '600', color: '#1a2e1a', fontSize: '14px' },
  txMeta: { display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7c6b' },
  txCat: { background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  txNotes: { fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' },
  txRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 },
  txAmount: { fontWeight: '700', fontSize: '15px' },
  txActions: { display: 'flex', gap: '4px' },
  txEditBtn: { background: 'none', border: '1px solid #e5e7eb', color: '#6b7c6b', padding: '3px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  txDeleteBtn: { background: 'none', border: '1px solid #fecaca', color: '#dc2626', padding: '3px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  // Plaid + CSV styles
  importBar: { background: 'white', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  importBarLeft: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  importBarLabel: { fontSize: '13px', fontWeight: '600', color: '#6b7c6b', whiteSpace: 'nowrap' },
  plaidBtn: { background: '#1a2e1a', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  csvBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'inline-block' },
  plaidError: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', marginTop: '8px' },
  reviewCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid #2d4a2d' },
  reviewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' },
  reviewTitle: { margin: '0 0 4px', fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  reviewSub: { margin: 0, fontSize: '13px', color: '#6b7c6b' },
  reviewActions: { display: 'flex', gap: '8px', marginBottom: '12px' },
  selectAllBtn: { background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  reviewList: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto', marginBottom: '14px' },
  reviewRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: '#f9fafb', borderRadius: '8px', flexWrap: 'wrap' },
  reviewDate: { fontSize: '12px', color: '#6b7c6b', flexShrink: 0, width: '90px' },
  reviewDesc: { flex: 1, fontSize: '13px', color: '#1a2e1a', fontWeight: '500', minWidth: '150px' },
  reviewSelect: { padding: '5px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'inherit' },
  reviewAmt: { fontWeight: '700', fontSize: '13px', flexShrink: 0, width: '80px', textAlign: 'right' },
  reviewFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid #e5e7eb' },
  confirmImportBtn: { padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#2d4a2d', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  unassignedWarning: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#713f12', marginBottom: '12px' },
}
